import _ from 'lodash'
import Backbone from 'backbone'
import constants from 'scoreboard/components/constants'
import formHelper from 'scoreboard/components/form-helper'
import i18n from 'scoreboard/components/i18n'
import selects from 'scoreboard/components/selects'
import dropdown from 'scoreboard/components/dropdown'
import template from './schedule-picker.hbs'
import momentHelper from 'scoreboard/components/moment-helper'

const dayOfWeekOptions = [
	{id: 'sunday', label: 'alerts.sunday'},
	{id: 'monday', label: 'alerts.monday'},
	{id: 'tuesday', label: 'alerts.tuesday'},
	{id: 'wednesday', label: 'alerts.wednesday'},
	{id: 'thursday', label: 'alerts.thursday'},
	{id: 'friday', label: 'alerts.friday'},
	{id: 'saturday', label: 'alerts.saturday'}
]

const dayOption = {id: 'not-specified', label: 'common.day'}

const dayOfMonthOptions = [
	{id: '1', label: 'admin.import.schedule.nth.1'},
	{id: '2', label: 'admin.import.schedule.nth.2'},
	{id: '3', label: 'admin.import.schedule.nth.3'},
	{id: '4', label: 'admin.import.schedule.nth.4'},
	{id: '5', label: 'admin.import.schedule.nth.5'},
	{id: '6', label: 'admin.import.schedule.nth.6'},
	{id: '7', label: 'admin.import.schedule.nth.7'},
	{id: '8', label: 'admin.import.schedule.nth.8'},
	{id: '9', label: 'admin.import.schedule.nth.9'},
	{id: '10', label: 'admin.import.schedule.nth.10'},
	{id: '11', label: 'admin.import.schedule.nth.11'},
	{id: '12', label: 'admin.import.schedule.nth.12'},
	{id: '13', label: 'admin.import.schedule.nth.13'},
	{id: '14', label: 'admin.import.schedule.nth.14'},
	{id: '15', label: 'admin.import.schedule.nth.15'},
	{id: '16', label: 'admin.import.schedule.nth.16'},
	{id: '17', label: 'admin.import.schedule.nth.17'},
	{id: '18', label: 'admin.import.schedule.nth.18'},
	{id: '19', label: 'admin.import.schedule.nth.19'},
	{id: '20', label: 'admin.import.schedule.nth.20'},
	{id: '21', label: 'admin.import.schedule.nth.21'},
	{id: '22', label: 'admin.import.schedule.nth.22'},
	{id: '23', label: 'admin.import.schedule.nth.23'},
	{id: '24', label: 'admin.import.schedule.nth.24'},
	{id: '25', label: 'admin.import.schedule.nth.25'},
	{id: '26', label: 'admin.import.schedule.nth.26'},
	{id: '27', label: 'admin.import.schedule.nth.27'},
	{id: '28', label: 'admin.import.schedule.nth.28'}
]

const lastDayOption = {id: 'L', label: 'admin.import.schedule.nth.L'}

/**
 * Enum of intervals shown in the dropdown.
 * Sometimes the schedule picker must omit certain intervals (e.g. scheduled exports)
 * This is meant to provide callers a way to provide an interval and keep
 * all details about the intervals encapsulated inside this component
 */
const intervals = {
	// Matches index position of select options
	QUARTER: 0,
	MONTH: 1,
	WEEK: 2,
	DAY: 3,
	TWELVE_HOURS: 4,
	SIX_HOURS: 5,
	THREE_HOURS: 6,
	HOUR: 7,
	MINUTE: 8

}

const renderTimeZoneSelect = function (elm, placeholderCode, selectedTz) {
	let options = []
	_.each(constants.timeZones, (label, value) => {
		let option = { value, label }
		if (value == selectedTz) {
			option.selected = true
		}
		options.push(option)
	})
	// filtering select because there are a lot of timezones!
	selects.filtering(elm, {
		options,
		placeholder: i18n.get(placeholderCode),
		// the time zones are already sorted, so don't sort again TYVM
		sort: []
	})
}

const SchedulePicker = Backbone.View.extend({

	className: 'schedule-picker',

	events: {
		'change [name=frequency]': 'selectsDisplay',
		'change [name=dayOfMonth]': 'validateDayOfMonth'
	},

	/**
	 *
	 * @property {String} model.timeZone
	 *
	 * @param {ScheduleData.kt} options.data The form data (schedule attributes)
	 *
	 * @param {intervals} [options.limitInterval=MINUTE]
	 * 	Hide all intervals smaller than this (exclusive), to hide MINUTE and HOUR, provide DAY
	 *
	 */
	initialize(options = {limitInterval: intervals.MINUTE}) {
		this.options = options
		const {timeZone} = _.get(this.model, 'attributes') || {}
		this.selectedTz = timeZone || momentHelper.defaultTimeZone()
		this.data = this.options.data || {}
		this.limitInterval = this.options.limitInterval
	},

	render: function() {
		this.$el.empty().append(template())
		selects.update(this.$('select'))

		if (this.limitInterval < intervals.MINUTE) {
			this.$('[name=frequency]').children().slice(this.limitInterval+1).remove()
		}

		formHelper.writeToDom(this.data, this.el)
		// AFTER writeToDom so the proper values are displayed, but before
		// renderTimeZoneSelect so the filtering select isn't destroyed
		selects(this.$('select'))
		renderTimeZoneSelect(this.$('[name=timeZone]'),
			'admin.import.schedule.selectTimezone', this.selectedTz)
		// must be AFTER formHelper.writeToDom
		this.selectsDisplay()

		return this
	},

	readFromDom() {
		return formHelper.readFromDom(this.el)
	},

	selectsDisplay: function () {
		let frequency = this.$('[name=frequency]').val()
		let dayOfWeek = this.$('[name=dayOfWeek]')
		let dayOfMonth = this.$('[name=dayOfMonth]')
		// the value to be pre-selected in the dayOfMonth dropdown
		// Passing in undefined means the first option will be selected.  We
		// don't want to pass in a null because that will select a fake blank
		// option.
		let dayOfMonthVal = dayOfMonth.val() || this.data.dayOfMonth || undefined
		let dayOfWeekVal = dayOfWeek.val() || this.data.dayOfWeek || undefined
		switch (frequency) {
			case 'quarter': {
				this.$('.day-of-month, .day-of-week, .hour-of-day, .minute-of-hour, ' +
					'.am-pm, .time-zone, .time-selectors, .on-the-label')
					.removeClass('is-not-displayed')

				this.$('.every-x-minutes, .on-label').addClass('is-not-displayed')
				dayOfWeek.prop('disabled', true)
				// update selections for day of week and day of month
				dropdown.idLabels(dayOfWeek.empty(), [dayOption])
				dropdown.idLabels(dayOfMonth.empty(), dayOfMonthOptions, dayOfMonthVal)

				let hasNoChildren = this.$('.day-of-month .items').children().length === 0
				this.$('.day-of-month').toggleClass('is-empty', hasNoChildren)

				break
			}
			case 'month': {
				// hide everyXminutes, show all others
				this.$('.day-of-month, .day-of-week, .hour-of-day, .minute-of-hour, ' +
					'.am-pm, .time-zone, .time-selectors, .on-the-label')
					.removeClass('is-not-displayed')

				this.$('.every-x-minutes, .on-label').addClass('is-not-displayed')
				dayOfWeek.prop('disabled', false)
				// update selections for day of week and day of month
				let weekOptions = _.cloneDeep(dayOfWeekOptions)
				weekOptions.unshift(dayOption)
				dropdown.idLabels(dayOfWeek.empty(), weekOptions, dayOfWeekVal)
				let monthOptions = _.clone(dayOfMonthOptions)
				monthOptions.push(lastDayOption)
				dropdown.idLabels(dayOfMonth.empty(), monthOptions, dayOfMonthVal)
				this.validateDayOfMonth()
				break
			}
			case 'week': {
				// hide dayOfMonth and everyXminutes, show all others
				this.$('.day-of-week, .hour-of-day, .minute-of-hour, .am-pm, .time-zone, .time-selectors, .on-label')
					.removeClass('is-not-displayed')

				this.$('.day-of-month, .every-x-minutes, .on-the-label')
					.addClass('is-not-displayed')

				// update day of week selections
				let validDayOfWeek = _.some(dayOfWeekOptions, { id: dayOfWeekVal })
				if (!validDayOfWeek) {
					dayOfWeekVal = undefined // just select the first option
				}
				dropdown.idLabels(dayOfWeek.empty(), dayOfWeekOptions, dayOfWeekVal)
				dayOfWeek.attr('disabled', false)
				selects(dayOfWeek)
				break
			}
			case 'day':
			case 'twelve-hours':
			case 'six-hours':
			case 'three-hours': {
				// display hour of day, minute of hour, am/pm, time zone
				this.$('.hour-of-day, .minute-of-hour, .am-pm, .time-zone, .time-selectors')
					.removeClass('is-not-displayed')
				// hide day of month, day of week, everyXminutes
				this.$('.day-of-month, .day-of-week, .every-x-minutes, .on-the-label, .on-label')
					.addClass('is-not-displayed')
				break
			}
			case 'hour': {
				// hide all but minute of hour
				this.$('.minute-of-hour, .time-selectors')
					.removeClass('is-not-displayed')

				this.$('.day-of-month, .day-of-week, .hour-of-day, .am-pm, .every-x-minutes, .time-zone, .on-the-label, .on-label')
					.addClass('is-not-displayed')
				break
			}
			case 'minute': {
				// hide all but every x minutes
				this.$('.every-x-minutes')
					.removeClass('is-not-displayed')

				this.$('.day-of-month, .day-of-week,.hour-of-day, .minute-of-hour, .am-pm, .time-zone, .time-selectors, .on-the-label, .on-label')
					.addClass('is-not-displayed')
				break
			}
			default: {
				break
			}
		}
	},

	validateDayOfMonth: function () {
		// don't allow a user to specify "the 14th Saturday of the month",
		// nor should we allow "the 5th saturday" because that's not guaranteed to always happen either
		let dayOfMonth = this.$('[name=dayOfMonth]').val()
		let frequency = this.$('[name=frequency]').val()
		let dayOfWeek = this.$('[name=dayOfWeek]')
		let invalidSelection =
			frequency === 'quarter' ||
			(frequency === 'month' && dayOfMonth != 'L' && dayOfMonth > 4)
		if (invalidSelection) {
			dayOfWeek.val('not-specified')
		}
		dayOfWeek.attr('disabled', invalidSelection)
		selects(dayOfWeek)
	}

})

_.extend(SchedulePicker, { intervals, renderTimeZoneSelect })
export default SchedulePicker
export { intervals }
export { renderTimeZoneSelect }

SchedulePicker.__filename__ = 'components/schedule-picker.js'
